import { createContext, useContext, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

type KeywordRule = {
  phrase: string;
  to: string;
  everyMention?: boolean;
};

type LinkContextValue = {
  used: Set<string>;
  currentPath: string;
};

const InternalLinkContext = createContext<LinkContextValue | null>(null);

export const INTERNAL_KEYWORD_LINKS: KeywordRule[] = [
  { phrase: 'free credit slot terbaru', to: '/free-credit-slot-terbaru/' },
  { phrase: 'sportsbook betting', to: '/sportsbook-betting/' },
  { phrase: 'football betting', to: '/football-betting/' },
  { phrase: 'sports betting', to: '/sports-betting/' },
  { phrase: 'free cuci 50', to: '/free-cuci-50/' },
  { phrase: 'slot casino', to: '/slot-casino/' },
  { phrase: 'cuci 100', to: '/cuci-100/' },
  { phrase: 'cuci 50', to: '/cuci-50/' },
  { phrase: 'free cuci', to: '/free-cuci/' },
  { phrase: 'MrCuci Malaysia', to: '/', everyMention: true },
  { phrase: 'MrCuci', to: '/', everyMention: true },
];

function normalizePath(path: string) {
  if (path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

function isWordBoundary(text: string, start: number, end: number) {
  const before = start === 0 ? '' : text[start - 1];
  const after = end >= text.length ? '' : text[end];
  const letterOrNumber = /[A-Za-z0-9]/;
  return !letterOrNumber.test(before) && !letterOrNumber.test(after);
}

function linkify(text: string, { used, currentPath }: LinkContextValue): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    let best: { index: number; length: number; phrase: string; to: string; id: string; everyMention?: boolean } | null =
      null;

    for (const rule of INTERNAL_KEYWORD_LINKS) {
      if (normalizePath(rule.to) === normalizePath(currentPath)) continue;
      const id = rule.phrase.toLowerCase();
      if (!rule.everyMention && used.has(id)) continue;

      const lower = remaining.toLowerCase();
      const target = rule.phrase.toLowerCase();
      let from = 0;
      while (from < lower.length) {
        const index = lower.indexOf(target, from);
        if (index === -1) break;
        if (isWordBoundary(remaining, index, index + rule.phrase.length)) {
          if (!best || index < best.index || (index === best.index && rule.phrase.length > best.length)) {
            best = {
              index,
              length: rule.phrase.length,
              phrase: remaining.slice(index, index + rule.phrase.length),
              to: rule.to,
              id,
              everyMention: rule.everyMention,
            };
          }
          break;
        }
        from = index + 1;
      }
    }

    if (!best) {
      nodes.push(remaining);
      break;
    }

    if (best.index > 0) {
      nodes.push(remaining.slice(0, best.index));
    }

    if (!best.everyMention) {
      used.add(best.id);
    }

    nodes.push(
      <Link key={`kw-${key++}`} to={best.to} className="text-[#d4a017] hover:underline">
        {best.phrase}
      </Link>
    );
    remaining = remaining.slice(best.index + best.length);
  }

  return nodes;
}

export function InternalLinkProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const value = { used: new Set<string>(), currentPath: pathname };
  return <InternalLinkContext.Provider value={value}>{children}</InternalLinkContext.Provider>;
}

export function LinkedText({ children }: { children: string }) {
  const context = useContext(InternalLinkContext);
  if (!children) return null;
  if (!context) return children;
  return <>{linkify(children, context)}</>;
}
